import React, { useEffect, useState } from "react";
import { api } from "../../lib/nurseryApi";
import { ChartLineUp, TrendUp, TrendDown, Wallet, Robot } from "@phosphor-icons/react";
import BackToOverview from "../../components/BackToOverview";

export default function AdminFinance() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/finance").then((r) => setData(r.data));
  }, []);

  if (!data) {
    return (
      <div className="fade-up">
        <BackToOverview />
        <h1 className="page-title">Financials</h1>
        <div className="card" style={{ padding: 24, marginTop: 16 }}>Loading…</div>
      </div>
    );
  }

  const margin = data.mrr - data.ai_cost_month;
  const marginPct = data.mrr > 0 ? Math.round((margin / data.mrr) * 100) : 0;

  return (
    <div data-testid="admin-finance-page" className="fade-up">
      <BackToOverview />
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--secondary)" }}>Money in vs money out</div>
      <h1 className="page-title" style={{ marginTop: 8 }}>Financials</h1>
      <p style={{ color: "var(--muted-fg)", marginTop: 6, fontSize: 14.5 }}>Subscription revenue minus AI infrastructure cost. Track margin per nursery.</p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginTop: 24 }}>
        <BigStat icon={Wallet} label="MRR (revenue)" value={`$${data.mrr.toLocaleString()}`} hint={`${data.paying_nurseries} paying nurseries`} color="var(--primary)" />
        <BigStat icon={Robot} label="AI cost (this month)" value={`$${data.ai_cost_month.toFixed(2)}`} hint={`${data.ai_calls_month} API calls`} color="var(--secondary)" />
        <BigStat icon={margin >= 0 ? TrendUp : TrendDown} label="Gross margin" value={`$${margin.toLocaleString()}`} hint={`${marginPct}% of MRR`} color={margin >= 0 ? "var(--primary)" : "#A14747"} />
        <BigStat icon={ChartLineUp} label="ARR (annualised)" value={`$${(data.mrr * 12).toLocaleString()}`} hint="MRR × 12" color="var(--accent)" />
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted-fg)", marginTop: 32, marginBottom: 12 }}>Cost per nursery (this month)</div>
      {data.per_nursery.length === 0 ? (
        <div className="card" style={{ padding: 40, textAlign: "center" }}>
          <Robot size={36} weight="duotone" color="var(--muted-fg)" />
          <h3 className="font-display" style={{ fontSize: 20, marginTop: 12 }}>No AI usage tracked yet</h3>
          <p style={{ color: "var(--muted-fg)", marginTop: 6, fontSize: 13.5 }}>
            Costs will populate here automatically once nurseries start using the AI scanner or chatbot.
          </p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ background: "var(--bg-alt)", textAlign: "left" }}>
                <th style={th}>Nursery</th>
                <th style={th}>Plan</th>
                <th style={th}>AI calls</th>
                <th style={th}>Cost</th>
                <th style={th}>Revenue</th>
                <th style={th}>Margin</th>
              </tr>
            </thead>
            <tbody>
              {data.per_nursery.map((n) => {
                const rev = n.plan_revenue || 0;
                const m = rev - n.cost;
                return (
                  <tr key={n.nursery_id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`finance-row-${n.nursery_id}`}>
                    <td style={td}><b>{n.nursery_name}</b></td>
                    <td style={td}><span className="badge badge-soft">{n.plan || "none"}</span></td>
                    <td style={td}>{n.calls}</td>
                    <td style={td}>${n.cost.toFixed(2)}</td>
                    <td style={td}>${rev}</td>
                    <td style={{ ...td, color: m >= 0 ? "var(--primary)" : "#A14747", fontWeight: 700 }}>
                      ${m.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="card-soft" style={{ marginTop: 24, padding: 16, background: "var(--accent-soft)", borderRadius: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#8B6E1A", letterSpacing: "0.1em", textTransform: "uppercase" }}>How it's tracked</div>
        <div style={{ fontSize: 13.5, color: "var(--text)", marginTop: 6, lineHeight: 1.5 }}>
          Each OpenRouter call (scanner + chatbot) logs token usage and estimated cost into the <code>ai_costs</code> collection.
          Revenue is the sum of paid plans assigned in <a href="/admin/subscriptions" style={{ color: "var(--secondary)" }}>Subscriptions</a>.
        </div>
      </div>
    </div>
  );
}

function BigStat({ icon: Icon, label, value, hint, color }) {
  return (
    <div className="stat-tile">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted-fg)" }}>{label}</div>
        <div style={{ width: 38, height: 38, borderRadius: 12, background: "var(--bg-alt)", display: "grid", placeItems: "center" }}>
          <Icon size={20} weight="bold" color={color} />
        </div>
      </div>
      <div className="font-display" style={{ fontSize: 36, marginTop: 10, lineHeight: 1, color }}>{value}</div>
      {hint && <div style={{ marginTop: 8, fontSize: 11.5, color: "var(--muted-fg)" }}>{hint}</div>}
    </div>
  );
}

const th = { padding: "12px 16px", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--muted-fg)" };
const td = { padding: "14px 16px", verticalAlign: "top" };
